import React, { useEffect, useState } from 'react'
import { BreadcrumbProps } from '@blueprintjs/core'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import {
    AdManageIcon,
    AutomationsIcon,
    CompassIcon,
    DashboardIcon,
    HistoryIcon,
    SettingsIcon,
} from '../../icons/icons'
import { Classes, Popover } from '../ui/popover'
// breadrcumbs has higher priority then source
// meaning if both are provided, breadcrumbs will override source
interface BreadcrumbsProps {
    source: any
    breadcrumbs: BreadcrumbProps[]
    hideIcon?: boolean
    className?: string
    fullWidth?: boolean
    style?: React.CSSProperties
}

export const Breadcrumbs = ({
    breadcrumbs = [],
    source,
    hideIcon = false,
    className,
    fullWidth = true,
    style,
}: BreadcrumbsProps) => {
    // we use this to force the Breadcrumbs to properly resize
    const [resizeCount, setResizeCount] = useState<number>(0)
    const items =
        breadcrumbs.length > 0 ? truncateBreadcrumbs(breadcrumbs) : extractDataFromSource(source)

    const handleResize = () => {
        setResizeCount((p) => p + 1)
    }

    useEffect(() => {
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    return (
        <nav
            id="breadcrumbs"
            key={resizeCount}
            aria-label="Breadcrumb"
            style={style}
            className={classNames(
                'mj-breadcrumbs flex min-w-0 items-center gap-1.5',
                fullWidth && 'w-full',
                className,
            )}
        >
            {!hideIcon && renderIcon(items)}
            {items.map((item, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <span className="mj-breadcrumb-separator">/</span>}
                    {item.href ? (
                        <Link
                            to={item.href}
                            className={classNames('mj-breadcrumb-link', item.className)}
                        >
                            {item.text}
                        </Link>
                    ) : (
                        <span className={classNames('mj-breadcrumb-current', item.className)}>
                            {item.text}
                        </span>
                    )}
                </React.Fragment>
            ))}
        </nav>
    )
}

const renderIcon = (items: BreadcrumbProps[]): JSX.Element | null => {
    if (items.length == 0) {
        return null
    }

    // get the first item
    const item = items[0]
    const text = item?.text

    if (!text) {
        return null
    }

    if (typeof text != 'string') {
        return null
    }

    switch (text.toLowerCase()) {
        case 'tools':
            return <AutomationsIcon isActive />
        case 'ad manager':
        case 'manage':
            return <AdManageIcon isActive />
        case 'overview':
            return <DashboardIcon isActive />
        case 'history':
            return <HistoryIcon />
        case 'ad account settings':
        case 'account settings':
            return <SettingsIcon />
        default:
            return null
    }
}

// this func tries to pull out the breadcrumbs from an item like:
// - keyword
// - target
// - ad_group
// - campaign
const extractDataFromSource = (source: any = null): BreadcrumbProps[] => {
    const breadcrumbs: BreadcrumbProps[] = []
    if (source == null) {
        return breadcrumbs
    }

    if (typeof source == 'string') {
        return [
            {
                href: ``,
                text: source,
            },
        ]
    }

    if (source?.campaign_name || (!!source?.campaign_id && source?.name)) {
        breadcrumbs.push({
            href: `/campaigns/${source.campaign_id}`,
            text: source.campaign_name ?? source?.name,
        })
    }

    if (source?.ad_group_name || (!!source?.ad_group_id && source?.name)) {
        breadcrumbs.push({
            href: `/ad-groups/${source.ad_group_id}`,
            text: source?.ad_group_name ?? source?.name,
        })
    }

    if (source?.keyword_text) {
        breadcrumbs.push({
            text: 'Keyword:',
        })
    }

    if (source?.targeting_expression) {
        breadcrumbs.push({
            text: 'Target:',
        })
    }

    return truncateBreadcrumbs(breadcrumbs)
}

// The popover portals out of the crumb, so the crumb's blur class does not reach it.
const truncateName = (input: string, className?: string, maxLength = 30) => {
    if (input.length > maxLength) {
        return (
            <Popover
                position="bottom"
                popoverClassName={Classes.POPOVER2_CONTENT_SIZING}
                interactionKind="hover"
                content={<span className={className}>{input}</span>}
            >
                <span>{input.slice(0, maxLength) + '...'}</span>
            </Popover>
        )
    }

    return input
}

const truncateBreadcrumbs = (breadcrumbs: BreadcrumbProps[]): BreadcrumbProps[] => {
    breadcrumbs = breadcrumbs.map((item, index) => {
        if (index == breadcrumbs.length - 1) {
            return item
        }

        item.text = truncateName(item.text as string, item.className)
        return item
    })

    return breadcrumbs
}
